import { useLayoutEffect, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, RotateCcw, SlidersHorizontal, X } from "lucide-react";
import type { NavigationFilters } from "./navigationState";
import "./groupedActiveFilters.css";

type Key = keyof NavigationFilters;
export type ActiveFilterGroup = { key: Key; label: string; values: string[] };
const copy = {
  tr: { filters: "Aktif filtreler", selected: "seçili", others: "Diğer aktif filtreler", clear: "Temizle", clearAll: "Tümünü temizle", close: "Kapat", remove: "filtresini kaldır", clearGroup: "filtrelerini temizle", otherGroups: "diğer filtre grubu" },
  en: { filters: "Active filters", selected: "selected", others: "Other active filters", clear: "Clear", clearAll: "Clear all", close: "Close", remove: "remove filter", clearGroup: "clear filters", otherGroups: "other filter groups" },
  ru: { filters: "Активные фильтры", selected: "выбрано", others: "Другие активные фильтры", clear: "Очистить", clearAll: "Очистить всё", close: "Закрыть", remove: "удалить фильтр", clearGroup: "очистить фильтры", otherGroups: "других групп фильтров" },
  uz: { filters: "Faol filtrlar", selected: "tanlangan", others: "Boshqa faol filtrlar", clear: "Tozalash", clearAll: "Barchasini tozalash", close: "Yopish", remove: "filtrini olib tashlash", clearGroup: "filtrlarini tozalash", otherGroups: "boshqa filtr guruhi" },
};

export default function GroupedActiveFilters({ groups, language, disabled, onRemove, onClearAll }: {
  groups: ActiveFilterGroup[]; language: string; disabled: boolean;
  onRemove: (key: Key, value?: string) => void; onClearAll: () => void;
}) {
  const c = copy[language as keyof typeof copy] ?? copy.tr;
  const container = useRef<HTMLDivElement>(null);
  const measures = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(groups.length);
  const [openKey, setOpenKey] = useState<string | null>(null);

  useLayoutEffect(() => {
    function fit() {
      if (!container.current || !measures.current) return;
      if (groups.length === 1) { setVisibleCount(1); return; }
      const widths = Array.from(measures.current.querySelectorAll<HTMLElement>("[data-measure]")).map(node => node.getBoundingClientRect().width);
      const available = container.current.clientWidth - 22 - 34; // scope icon, global clear and gaps
      const total = widths.reduce((sum, width) => sum + width + 6, 0);
      if (total <= available) { setVisibleCount(groups.length); return; }
      const room = available - 45; // overflow trigger, with one-digit group count
      let used = 0, count = 0;
      for (const width of widths) { if (used + width + 6 > room) break; used += width + 6; count++; }
      setVisibleCount(count);
    }
    fit();
    const observer = new ResizeObserver(fit);
    if (container.current) observer.observe(container.current);
    measures.current?.querySelectorAll<HTMLElement>("[data-measure]").forEach(node => observer.observe(node));
    return () => observer.disconnect();
  }, [groups]);

  const visible = groups.slice(0, visibleCount), overflow = groups.slice(visibleCount);
  useLayoutEffect(() => {
    if (openKey === "overflow" && !overflow.length) setOpenKey(null);
    else if (openKey && openKey !== "overflow" && !visible.some(group => group.key === openKey)) setOpenKey(null);
  }, [openKey, overflow.length, visible]);

  function values(group: ActiveFilterGroup) {
    return <div className={`caf-values${group.values.some(value => value.length > 24) ? " caf-values--long" : ""}`}>
      {group.values.map(value => <button type="button" key={value} disabled={disabled} aria-label={`${value} · ${group.label} · ${c.remove}`} title={value}
        onClick={() => onRemove(group.key, value)}><span>{value}</span><X size={12} aria-hidden="true" /></button>)}
    </div>;
  }
  function content(group: ActiveFilterGroup) {
    return <>
      <header className="caf-popover-heading"><strong>{group.label}</strong><span>{group.values.length} {c.selected}</span><Popover.Close asChild><button type="button" aria-label={c.close}><X size={15} aria-hidden="true" /></button></Popover.Close></header>
      {values(group)}
      <footer className="caf-popover-footer"><button type="button" disabled={disabled} aria-label={`${group.label} · ${c.clearGroup}`} onClick={() => onRemove(group.key)}>{group.label} · {c.clear}</button></footer>
    </>;
  }
  return <div className="compact-active-filters" ref={container} role="group" aria-label={c.filters}>
    <SlidersHorizontal className="caf-scope-icon" size={15} aria-hidden="true" />
    <div className="caf-measures" ref={measures} aria-hidden="true">
      {groups.map(group => <span data-measure key={group.key} className="caf-measure"><span>{group.label}</span><small>{group.values.length} {c.selected}</small><ChevronDown size={12} /><X size={12} /></span>)}
    </div>
    <div className="caf-items">
      {visible.map(group => <Popover.Root key={group.key} open={openKey === group.key} onOpenChange={open => setOpenKey(open ? group.key : null)}>
        <div className="caf-group">
          <Popover.Trigger asChild><button type="button" className="caf-group-trigger" disabled={disabled} aria-label={`${group.label} · ${group.values.length} ${c.selected}`}>
            <strong title={group.label}>{group.label}</strong><span>{group.values.length} {c.selected}</span><ChevronDown size={12} aria-hidden="true" />
          </button></Popover.Trigger>
          <button type="button" className="caf-group-clear" disabled={disabled} aria-label={`${group.label} · ${c.clearGroup}`} title={`${group.label} · ${c.clear}`} onClick={() => onRemove(group.key)}><X size={12} aria-hidden="true" /></button>
        </div>
        <Popover.Portal><Popover.Content className="caf-popover" align="start" sideOffset={9} collisionPadding={12} aria-label={group.label}>{content(group)}</Popover.Content></Popover.Portal>
      </Popover.Root>)}
      {overflow.length ? <Popover.Root open={openKey === "overflow"} onOpenChange={open => setOpenKey(open ? "overflow" : null)}>
        <Popover.Trigger asChild><button type="button" className="caf-overflow" disabled={disabled} aria-label={`${overflow.length} ${c.otherGroups}`} title={c.others}>+{overflow.length}<ChevronDown size={11} aria-hidden="true" /></button></Popover.Trigger>
        <Popover.Portal><Popover.Content className="caf-popover caf-popover--overflow" align="end" sideOffset={9} collisionPadding={12} aria-label={c.others}>
          <header className="caf-popover-heading"><strong>{c.others}</strong><Popover.Close asChild><button type="button" aria-label={c.close}><X size={15} aria-hidden="true" /></button></Popover.Close></header>
          <div className="caf-overflow-groups">{overflow.map(group => <section key={group.key} aria-label={group.label}>
            <div className="caf-overflow-heading"><strong>{group.label}</strong><span>{group.values.length} {c.selected}</span><button type="button" disabled={disabled} aria-label={`${group.label} · ${c.clearGroup}`} onClick={() => onRemove(group.key)}>{c.clear}</button></div>
            {values(group)}
          </section>)}</div>
        </Popover.Content></Popover.Portal>
      </Popover.Root> : null}
    </div>
    <button type="button" className="caf-clear-all" disabled={disabled} onClick={onClearAll} aria-label={c.clearAll} title={c.clearAll}><RotateCcw size={14} aria-hidden="true" /></button>
  </div>;
}
